import React, { useEffect, useState } from 'react';
import _ from 'lodash';

import {
  Autocomplete,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
} from '@mui/material';
import { Search } from '@mui/icons-material';

const getOptionValue = (option) => {
  if (option == null || typeof option !== 'object') return option;
  return option.value ?? option.id ?? option.name;
};

const getAutocompleteLabel = (option) => {
  if (option == null) return '';
  if (typeof option === 'string' || typeof option === 'number') return String(option);
  const label = option.name ?? option.title ?? getOptionValue(option);
  return label != null ? String(label) : '';
};

const normalizeOptions = (values) => {
  if (!values) return [];
  return Array.isArray(values) ? values : Object.values(values);
};

const findAutocompleteOption = (values, selectedValue) => {
  if (selectedValue == null || selectedValue === '') return null;
  const selected = getOptionValue(selectedValue);
  if (selected == null || selected === '') return null;
  const options = normalizeOptions(values);
  return _.find(
    options,
    (option) => String(getOptionValue(option)) === String(selected),
  ) || null;
};

const resolveAutocompleteValue = (values, selectedValue, multiple) => {
  if (multiple) {
    const selected = Array.isArray(selectedValue) ? selectedValue : [];
    return selected
      .map((item) => {
        const option = findAutocompleteOption(values, item);
        if (option) return option;
        if (item == null || item === '') return null;
        if (typeof item === 'object') return item;
        return { id: item, name: String(item) };
      })
      .filter(Boolean);
  }

  return findAutocompleteOption(values, selectedValue)
    || (selectedValue != null && selectedValue !== '' ? { name: String(selectedValue) } : null);
};

const InputForm = (props) => {
  const { form, setForm } = props;

  const {
    values, name, label, helperText, loading, type, sx, multiple,
  } = props;
  const { value: valueProps } = props;
  // events
  const { onOpen, onChange } = props;

  const { btnIcon } = props;
  const { btnDisabled } = props;
  const { inputError } = props;

  const [value, setValue] = useState(
    valueProps != null ? valueProps : (multiple ? [] : ''),
  );

  useEffect(() => {
    if (multiple) {
      setValue(Array.isArray(valueProps) ? valueProps : []);
      return;
    }
    setValue(valueProps != null ? valueProps : '');
  }, [valueProps, multiple]);

  const onClick = () => {
    setForm({
      ...(form || {}),
      page: 1,
      [name]: value,
    });
  };

  const formControlProps = {
    sx: sx || {},
    fullWidth: true,
  };
  const fieldProps = {
    fullWidth: true,
    sx: {
      ...sx,
      ...{
        pr: 0,
        bgcolor: 'background.paper',
      },
    },
    value: type === 'autocomplete'
      ? resolveAutocompleteValue(values, value, multiple)
      : value,
    multiple: !!multiple,
    disabled: !!btnDisabled,
  };
  const inputLabelProps = {};
  if (['autocomplete'].indexOf(type) === -1) {
    fieldProps.label = label;
    fieldProps.error = !!inputError;
    fieldProps.type = type || 'text';
  }

  if (['datetime-local', 'time', 'date'].indexOf(type) > -1) {
    inputLabelProps.shrink = true;
    fieldProps.onChange = (event) => {
      setValue(event.target.value);
      // onClick();
      setForm({
        ...(form || {}),
        page: 1,
        [name]: event.target.value,
      });
    };
  } else if (values && typeof values === 'object') {
    fieldProps.onChange = (event, newValue) => {
      if (type !== 'autocomplete') {
        const next = event.target.value;
        setValue(next);
        setForm({
          ...(form || {}),
          page: 1,
          [name]: next,
        });
        return;
      }
      let filterValue;
      if (multiple) {
        filterValue = _.uniqBy(
          (newValue || []).filter((item) => item != null && item !== ''),
          (item) => String(getOptionValue(item)),
        );
        setValue(filterValue);
        filterValue = _.compact(_.map(filterValue, (item) => getOptionValue(item)));
      } else {
        filterValue = getOptionValue(newValue) || '';
        setValue(filterValue);
      }
      setForm({
        ...(form || {}),
        page: 1,
        [name]: filterValue,
      });
    };
    if (typeof onOpen === 'function') {
      fieldProps.onOpen = onOpen;
    }
  } else {
    fieldProps.endAdornment = (
      <InputAdornment position="end">
        <Button variant="text" onClick={onClick} disabled={!!btnDisabled}>{btnIcon || <Search />}</Button>
      </InputAdornment>
    );
    fieldProps.onKeyPress = (event) => {
      if (event.key === 'Enter') {
        onClick();
      }
    };
    fieldProps.onChange = (event) => {
      if (typeof onChange === 'function') {
        onChange(event);
      }
      setValue(event.target.value);
    };
  }

  if (type === 'autocomplete') {
    fieldProps.options = normalizeOptions(values);
    fieldProps.getOptionLabel = getAutocompleteLabel;
    fieldProps.isOptionEqualToValue = (option, val) => (
      String(getOptionValue(option)) === String(getOptionValue(val))
    );
    fieldProps.disablePortal = true;

    fieldProps.renderInput = (params) => {
      const textFieldProps = { ...params, error: !!inputError, label };
      if (loading) {
        textFieldProps.InputProps = {
          ...params.InputProps,
          endAdornment: (
            <>
              <CircularProgress size={20} />
              {params.InputProps.endAdornment}
            </>
          ),
        };
      }
      return (<TextField {...textFieldProps} />);
    };

    return (<Autocomplete {...fieldProps} />);
  }

  return (
    <FormControl {...formControlProps}>
      <InputLabel {...inputLabelProps}>{fieldProps.label}</InputLabel>

      {values && typeof values === 'object' ? (
        <Select {...fieldProps}>
          {values.map((item, index) => (
            <MenuItem
              key={`${name || 'select'}-${item.value != null ? item.value : (item.id != null ? item.id : index)}`}
              value={item.value != null ? item.value : item.id}
            >
              {item.name}
            </MenuItem>
          ))}
        </Select>
      ) : (<OutlinedInput {...fieldProps} />)}

      {typeof helperText === 'string' && (<FormHelperText>{helperText}</FormHelperText>)}
    </FormControl>
  );
};

export default InputForm;
